/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { createDateRange, DateRangeInputFormik } from '@strata/react-date-time';
import { FormContainer } from '@strata/react-form-container';
import { FormInputContainer } from '@strata/react-form-input-container';
import { validationStrings } from '@strata/strata-ui-strings';
import dayjs from 'dayjs';
import { Formik } from 'formik';
import { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { postPreBid, putPreBid } from 'api/auctionsAPI';
import { UnsavedChangesDialog } from 'components/admin/automations/dialogs/UnsavedChangesDialog';
import { NewTemplateNote } from 'components/admin/automations/NewTemplateNote';
import { AutomationTemplateProps } from 'components/admin/automations/templates/AutomationTemplatePropsInterface';
import { FrozenPriceNote } from 'components/admin/automations/templates/FrozenPriceNote';
import { TemplateHeaderActions } from 'components/admin/automations/templates/TemplateHeaderActions';
import { ActionSection } from 'components/admin/automations/templateSections/ActionSection';
import { CriteriaSection } from 'components/admin/automations/templateSections/CriteriaSection';
import { DuplicateRuleMessage } from 'components/admin/automations/templateSections/DuplicateRuleMessage';
import { NameSection } from 'components/admin/automations/templateSections/NameSection';
import {
    ALL_PAYERS,
    AutomationRuleActions,
    AutomationRuleCriteria,
    AutomationRuleFormContext,
    findAutomationRuleCriterionIndex,
    findAutomationRuleCriterionValue,
    mapProductsToBrandAndGenericProducts,
    PerformanceCriteriaOptions,
} from 'services/AutomationRuleHelpers';
import { PlaceOfServiceCode } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppSelector } from 'store';
import { useGetPrescriberListsQuery, useGetStateListsQuery } from 'store/api/auctionApi';
import {
    PayerSegment,
    useGetConfiguredProductsForPayerQuery,
    useGetPayerSegmentsForSupplierQuery,
    useGetPlaceOfServiceMapQuery,
} from 'store/api/referenceApi';

const formatRuleName = (
    criteria: AutomationRuleFormContext['criteria'],
    action: AutomationRuleFormContext['action'],
) => {
    let ruleName = [
        AutomationRuleCriteria.PRODUCT,
        AutomationRuleCriteria.PAYER,
        AutomationRuleCriteria.PERFORMANCE,
        AutomationRuleCriteria.PLACE_OF_SERVICE,
        AutomationRuleCriteria.PRESCRIBER_LIST,
        AutomationRuleCriteria.STATE_LIST,
    ]
        .map((key) => findAutomationRuleCriterionValue(criteria, key)?.name)
        .filter((val) => val)
        .join(' & ');

    if (
        action?.type === AutomationRuleActions.SET_MAX_BID &&
        action.value?.some(
            ({ bidAmt, referentPriceType }) => typeof bidAmt === 'number' && referentPriceType,
        )
    ) {
        ruleName += ` = ${action.value
            .map(
                ({ bidAmt, referentPriceType }) =>
                    `${referentPriceType} ${bidAmt < 0 ? '-' : '+'} ${Math.abs(bidAmt)}`,
            )
            .join(' & ')}`;
    }

    return ruleName;
};

export const ProductTemplate = ({
    rule = undefined,
    rules = undefined,
    userCanEdit = false,
    onChange,
    onChangeRule,
    onSubmitSuccess,
    onDeleteSuccess,
    onDuplicate,
}: AutomationTemplateProps) => {
    const selectedNpi = useAppSelector((state) => state.auth.selectedNpi);
    const isExistingRule = !!rule?.id;
    const [showUnsavedChangedDialog, setShowUnsavedChangedDialog] = useState(false);

    const { data: segmentData, isLoading } = useGetPayerSegmentsForSupplierQuery({
        supplierNpi: selectedNpi!,
    });
    const segmentList = useMemo(
        () =>
            segmentData && [
                { id: ALL_PAYERS, payerId: ALL_PAYERS, name: 'All Health Plans' },
                ...segmentData,
            ],
        [segmentData],
    );

    const initialSelectedSegment = useMemo(
        () => segmentList?.find((segment) => segment.id === rule?.segmentId),
        [rule, segmentList],
    );

    const [selectedSegment, setSelectedSegment] = useState(initialSelectedSegment);

    const { data: configuredProductList } = useGetConfiguredProductsForPayerQuery(
        selectedSegment ? { segmentId: selectedSegment.id } : skipToken,
    );
    const uniqueMedicationList = useMemo(
        () => mapProductsToBrandAndGenericProducts(configuredProductList),
        [configuredProductList],
    );

    const { data: placeOfServiceMap } = useGetPlaceOfServiceMapQuery();
    const placeOfServiceOptions = useMemo(
        () =>
            Object.entries(placeOfServiceMap ?? {})
                // Filter out places of service specialty pharmacies do not interact with
                .filter(
                    ([key]) =>
                        !(
                            [
                                PlaceOfServiceCode.FACILITY,
                                PlaceOfServiceCode.INFU_INFU,
                                PlaceOfServiceCode.PHYS_PHYS,
                            ] as string[]
                        ).includes(key),
                )
                .map(([key, value]) => ({
                    id: key,
                    name: value,
                })),
        [placeOfServiceMap],
    );

    const { data: prescriberListData } = useGetPrescriberListsQuery();
    const { data: stateListData } = useGetStateListsQuery();

    const actionOptions = [AutomationRuleActions.SET_MAX_BID];

    const initialDateRange = useMemo(
        () =>
            createDateRange(
                rule?.startDate ?? dayjs().toISOString(),
                rule?.endDate ?? '12/31/2099',
            ),
        [rule?.startDate, rule?.endDate],
    );
    const initialValues = useMemo(
        () => ({
            criteria: [
                {
                    criterion: AutomationRuleCriteria.PAYER,
                    value: initialSelectedSegment,
                },
                {
                    criterion: AutomationRuleCriteria.PRODUCT,
                    value: uniqueMedicationList?.find(
                        (med) =>
                            med.oid === rule?.productServiceOid && med.isBrand === rule?.isBrand,
                    ),
                },
                ...(rule?.performance
                    ? [
                          {
                              criterion: AutomationRuleCriteria.PERFORMANCE,
                              value: PerformanceCriteriaOptions?.find(
                                  (option) => option.id === rule.performance,
                              ),
                          },
                      ]
                    : []),
                ...(rule?.placeOfService
                    ? [
                          {
                              criterion: AutomationRuleCriteria.PLACE_OF_SERVICE,
                              value: placeOfServiceOptions?.find(
                                  (option) => option.id === rule.placeOfService,
                              ),
                          },
                      ]
                    : []),
                ...(rule?.prescriberListId
                    ? [
                          {
                              criterion: AutomationRuleCriteria.PRESCRIBER_LIST,
                              value: prescriberListData?.find(
                                  (option) => option.prescriberListId === rule.prescriberListId,
                              ),
                          },
                      ]
                    : []),
                ...(rule?.stateListId
                    ? [
                          {
                              criterion: AutomationRuleCriteria.STATE_LIST,
                              value: stateListData?.find(
                                  (option) => option.stateListId === rule.stateListId,
                              ),
                          },
                      ]
                    : []),
            ],
            ...ActionSection.initialValues(rule, isExistingRule),
            dateRange: initialDateRange,
            ...NameSection.initialValues(rule),
        }),
        [
            initialSelectedSegment,
            uniqueMedicationList,
            rule,
            placeOfServiceOptions,
            prescriberListData,
            stateListData,
            isExistingRule,
            initialDateRange,
        ],
    );

    const submitRule = async (values: AutomationRuleFormContext) => {
        try {
            let id = rule?.id;
            const payload = {
                npi: selectedNpi!,
                id: id!,
                ...CriteriaSection.submitValues(values),
                ...ActionSection.submitValues(values),
                runOnce: false,
                startDate: formatDate(values.dateRange?.[0], DateFormats.HYPHEN_DATE),
                endDate: formatDate(values.dateRange?.[1], DateFormats.HYPHEN_DATE),
                ...NameSection.submitValues(values),
            };
            if (isExistingRule) {
                await putPreBid(payload);
            } else {
                id = (await postPreBid(payload))?.bid_id;
            }
            await onSubmitSuccess(id?.toLowerCase());
        } catch (err) {
            handleError('An error occurred saving rule. Please try again.', err as Error);
        }
    };

    const duplicateRule = (values: AutomationRuleFormContext) => {
        onDuplicate({
            template: rule?.template,
            ...CriteriaSection.duplicateValues(values),
            ...ActionSection.duplicateValues(values),
            startDate: formatDate(values.dateRange?.[0], DateFormats.HYPHEN_DATE),
            endDate: formatDate(values.dateRange?.[1], DateFormats.HYPHEN_DATE),
            ...NameSection.duplicateValues(values),
        });
    };

    return (
        <Formik
            onSubmit={submitRule}
            initialValues={initialValues as AutomationRuleFormContext}
            enableReinitialize
            validationSchema={Yup.object({
                ...CriteriaSection.validationSchema,
                ...ActionSection.validationSchema,
                dateRange: DateRangeInputFormik.validator().required(
                    validationStrings.requiredField.errorMessage,
                ),
                ...NameSection.validationSchema,
            })}
        >
            {(formik) => {
                onChange(formik.dirty);

                return (
                    <FormContainer
                        formTitle="Product Template"
                        onFormSubmit={formik.handleSubmit}
                        onCancelClick={(e) =>
                            formik.dirty ? setShowUnsavedChangedDialog(true) : formik.handleReset(e)
                        }
                        isLoading={isLoading}
                        isSubmitting={!(formik.isValid && formik.dirty) || formik.isSubmitting}
                        formHeaderRightContent={
                            <TemplateHeaderActions
                                rule={rule}
                                userCanEdit={userCanEdit}
                                isExistingRule={isExistingRule}
                                onDeleteSuccess={onDeleteSuccess}
                                duplicateRule={duplicateRule}
                            />
                        }
                        formLeftContent={
                            <div className="sds_automation-template">
                                <UnsavedChangesDialog
                                    isOpen={showUnsavedChangedDialog}
                                    onPrimaryButtonClick={(e) => {
                                        formik.handleReset(e);
                                        setShowUnsavedChangedDialog(false);
                                    }}
                                    onSecondaryButtonClick={() =>
                                        setShowUnsavedChangedDialog(false)
                                    }
                                />
                                <NewTemplateNote />
                                <FrozenPriceNote />
                                <CriteriaSection
                                    userCanEdit={userCanEdit}
                                    additionalCriteriaOptions={[
                                        //   AutomationRuleCriteria.PERFORMANCE,
                                        AutomationRuleCriteria.PLACE_OF_SERVICE,
                                        AutomationRuleCriteria.PRESCRIBER_LIST,
                                        AutomationRuleCriteria.STATE_LIST,
                                    ]}
                                    criteriaPropsMap={{
                                        [AutomationRuleCriteria.PAYER]: {
                                            values: segmentList,
                                            disabled: isExistingRule,
                                            onChange: async (newValue, arrayHelpers) => {
                                                setSelectedSegment(newValue as PayerSegment);
                                                const index = findAutomationRuleCriterionIndex(
                                                    formik.values.criteria,
                                                    AutomationRuleCriteria.PRODUCT,
                                                );
                                                await formik.setFieldValue(
                                                    `${arrayHelpers.name}.${index}.value`,
                                                    undefined,
                                                );
                                            },
                                        },
                                        [AutomationRuleCriteria.PRODUCT]: {
                                            values: uniqueMedicationList,
                                            onChange: () =>
                                                formik.setFieldValue('action.value', [
                                                    { tempId: uuidv4() },
                                                ]),
                                        },
                                        [AutomationRuleCriteria.PERFORMANCE]: {
                                            values: PerformanceCriteriaOptions,
                                        },
                                        [AutomationRuleCriteria.PLACE_OF_SERVICE]: {
                                            values: placeOfServiceOptions,
                                        },
                                        [AutomationRuleCriteria.PRESCRIBER_LIST]: {
                                            values: prescriberListData,
                                        },
                                        [AutomationRuleCriteria.STATE_LIST]: {
                                            values: stateListData,
                                        },
                                    }}
                                />
                                <DuplicateRuleMessage
                                    rule={rule}
                                    rules={rules}
                                    onChangeRule={onChangeRule}
                                />
                                <ActionSection
                                    actionOptions={actionOptions}
                                    userCanEdit={userCanEdit}
                                    isExistingRule={isExistingRule}
                                />
                                <FormInputContainer>
                                    <div className="sds_automation-template__row">
                                        <div className="sds_automation-template__label sds_label font-bold--uppercase">
                                            Run
                                        </div>
                                        <div style={{ gridColumnStart: 2, gridColumnEnd: 5 }}>
                                            <DateRangeInputFormik
                                                name="dateRange"
                                                disabled={!userCanEdit}
                                                required
                                                helperText="Single date range can be selected by entering same date for both."
                                            />
                                        </div>
                                    </div>
                                </FormInputContainer>
                                <NameSection
                                    userCanEdit={userCanEdit}
                                    formatRuleName={formatRuleName}
                                />
                            </div>
                        }
                    />
                );
            }}
        </Formik>
    );
};
